import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input, ViewChild, inject } from "@angular/core";
import { ConfirmCleanDialogComponent } from "../confirm-clean-dialog/confirm-clean-dialog.component";
import { EquipmentColumn, EquipmentGroup, EquipmentRow, EquipmentSheetDefinition } from "../../data/lab-equipment";
import { AuditRating, RATING_POINTS } from "../../models/lab-audit.models";
import { LabAuditStateService } from "../../services/lab-audit-state.service";

@Component({
  selector: "app-equipment-sheet",
  standalone: true,
  imports: [CommonModule, ConfirmCleanDialogComponent],
  templateUrl: "./equipment-sheet.component.html",
  styleUrl: "./equipment-sheet.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EquipmentSheetComponent {
  @ViewChild(ConfirmCleanDialogComponent) private cleanDialog?: ConfirmCleanDialogComponent;
  @Input({ required: true }) definition!: EquipmentSheetDefinition;
  readonly auditState = inject(LabAuditStateService);
  readonly ratings: Exclude<AuditRating, "">[] = ["F", "N", "G", "E"];

  get sectionState() {
    return this.auditState.audit()[this.definition.section];
  }

  get score(): number {
    return this.auditState.scoreFor(this.definition.section);
  }

  fieldId(groupId: string, rowId: string, columnKey: string): string {
    return `${groupId}.${rowId}.${columnKey}`;
  }

  fieldValue(groupId: string, row: EquipmentRow, column: EquipmentColumn): string {
    return this.sectionState.fields[this.fieldId(groupId, row.id, column.key)] ?? row.values?.[column.key] ?? "";
  }

  updateField(groupId: string, row: EquipmentRow, column: EquipmentColumn, value: string): void {
    this.auditState.updateSectionField(this.definition.section, this.fieldId(groupId, row.id, column.key), value);
  }

  selectField(groupId: string, row: EquipmentRow, column: EquipmentColumn, value: string): void {
    this.updateField(groupId, row, column, this.fieldValue(groupId, row, column) === value ? "" : value);
  }

  selectAnswer(row: EquipmentRow, answer: "yes" | "no"): void {
    const current = this.sectionState.responses[row.id]?.answer ?? "";
    this.auditState.updateResponse(this.definition.section, row.id, { answer: current === answer ? "" : answer });
  }

  selectRating(row: EquipmentRow, rating: Exclude<AuditRating, "">): void {
    const current = this.sectionState.responses[row.id]?.rating ?? "";
    this.auditState.updateResponse(this.definition.section, row.id, { rating: current === rating ? "" : rating });
  }

  points(row: EquipmentRow): number | null {
    const rating = this.sectionState.responses[row.id]?.rating;
    return rating ? RATING_POINTS[rating] : null;
  }

  hasRating(group: EquipmentGroup): boolean {
    return group.columns.some(column => column.kind === "rating");
  }

  updateCount(groupId: string, event: Event): void {
    this.auditState.updateSectionField(this.definition.section, `${groupId}.count`, (event.target as HTMLInputElement).value);
  }

  updateAdditionalComments(event: Event): void {
    this.auditState.updateAdditionalComments(this.definition.section, (event.target as HTMLTextAreaElement).value);
  }

  openCleanDialog(): void {
    this.cleanDialog?.open();
  }
}